import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import OverflowTooltip from '../../../shared/OverflowTooltip';
import { COLORS } from '../../../shared/colors';
import {
  CALENDAR_PRIORITY_COLOR_MAP,
  CALENDAR_STATUS_KEYWORDS,
  CALENDAR_STATUS_COLOR_MAP,
  CALENDAR_TASK_TYPE_KEYWORDS,
  CALENDAR_TASK_TYPE_COLOR_MAP,
  CALENDAR_COLOR_OPTION_LABELS
} from '../../../shared/appConstants';
import { store } from '../../../store';

// Colors to exclude from grouping assignments
const EXCLUDED_COLORS = ['#3b82f6', '#e2ecfe', '#023E8A', '#0ea5e9'];

// Color assignment cache to ensure unique colors per category
const colorAssignmentCache = new Map();

// Generate consistent color from string with category-specific offset and collision avoidance
export const generateColorFromString = (str, category = 'status') => {
  if (!str) return COLORS.calendarPalette.find(color => !EXCLUDED_COLORS.includes(color)) || COLORS.calendarPalette[0];
  
  // Create cache key
  const cacheKey = `${category}:${str}`;
  
  // Return cached color if exists
  if (colorAssignmentCache.has(cacheKey)) {
    return colorAssignmentCache.get(cacheKey);
  }
  
  // Get category-specific offset
  const offset = COLORS.calendarCategoryOffsets[category] || 0;
  
  // Get already used colors for this category
  const usedColors = new Set();
  for (const [key, color] of colorAssignmentCache.entries()) {
    if (key.startsWith(`${category}:`)) {
      usedColors.add(color);
    }
  }
  
  // Enhanced hash function to reduce collisions
  let hash = 0;
  const prime = 31;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * prime + str.charCodeAt(i)) % 2147483647;
  }
  
  // Add string length to hash to differentiate similar strings
  hash = (hash + str.length * 17) % 2147483647;
  
  // Filter out excluded colors from palette
  const availablePalette = COLORS.calendarPalette.filter(color => !EXCLUDED_COLORS.includes(color));
  
  // Try to find an unused color
  let attempts = 0;
  let paletteColor;
  
  do {
    const baseIndex = (Math.abs(hash) + attempts) % availablePalette.length;
    const paletteIndex = (baseIndex + offset) % availablePalette.length;
    paletteColor = availablePalette[paletteIndex];
    attempts++;
  } while (usedColors.has(paletteColor) && attempts < availablePalette.length);
  
  // Cache the result
  colorAssignmentCache.set(cacheKey, paletteColor);
  
  return paletteColor;
};

// Generate lightened color for backgrounds
export const lightenColor = (color, percent = 85) => {
  // Handle HSL colors
  if (color.startsWith('hsl')) {
    const match = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (match) {
      const [, h, s, l] = match;
      return `hsl(${h}, ${s}%, ${Math.min(95, parseInt(l) + 40)}%)`;
    }
  }
  
  // Handle hex colors
  const num = parseInt(color.replace('#', ''), 16);
  const r = (num >> 16) + Math.round((255 - (num >> 16)) * (percent / 100));
  const g = ((num >> 8) & 0x00FF) + Math.round((255 - ((num >> 8) & 0x00FF)) * (percent / 100));
  const b = (num & 0x0000FF) + Math.round((255 - (num & 0x0000FF)) * (percent / 100));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

export const getPriorityColor = (priority, { priorities = [] } = {}) => {
  if (priority && typeof priority === 'object' && (priority.colour || priority.color)) {
    return priority.colour || priority.color;
  }
  
  if (!priorities || priorities.length === 0) {
    try {
      priorities = store.getState().tickets?.dropdowns?.priorities || [];
    } catch (e) {
      priorities = [];
    }
  }

  const priorityId = typeof priority === 'string' ? priority : priority?.id;
  const priorityName = typeof priority === 'string' ? priority : priority?.name || '';
  
  const option = priorities.find(p => p.id === priorityId || p.name === priorityName);
  if (option && (option.colour || option.color)) {
    return option.colour || option.color;
  }

  const mappedColor = CALENDAR_PRIORITY_COLOR_MAP[priorityName];
  if (mappedColor) {
    return mappedColor;
  }
  return generateColorFromString(priorityName, 'priority');
};

export const getStatusColor = (status) => {
  if (status && typeof status === 'object' && (status.colour || status.color)) {
    return status.colour || status.color;
  }
  const statusName = typeof status === 'string' ? status : status?.name || '';
  const lowerStatus = statusName.toLowerCase();
  
  let mappedColor;
  if (CALENDAR_STATUS_KEYWORDS.TODO.some(kw => lowerStatus.includes(kw))) mappedColor = CALENDAR_STATUS_COLOR_MAP.TODO;
  else if (CALENDAR_STATUS_KEYWORDS.IN_PROGRESS.some(kw => lowerStatus.includes(kw))) mappedColor = CALENDAR_STATUS_COLOR_MAP.IN_PROGRESS;
  else if (CALENDAR_STATUS_KEYWORDS.REVIEW.some(kw => lowerStatus.includes(kw))) mappedColor = CALENDAR_STATUS_COLOR_MAP.REVIEW;
  else if (CALENDAR_STATUS_KEYWORDS.DONE.some(kw => lowerStatus.includes(kw))) mappedColor = CALENDAR_STATUS_COLOR_MAP.DONE;
  else if (CALENDAR_STATUS_KEYWORDS.BLOCKED.some(kw => lowerStatus.includes(kw))) mappedColor = CALENDAR_STATUS_COLOR_MAP.BLOCKED;
  
  if (mappedColor) {
    return mappedColor;
  }
  return generateColorFromString(statusName, 'status');
};

export const getTaskTypeColor = (taskType) => {
  const typeName = typeof taskType === 'string' ? taskType : taskType?.name || '';
  const lowerType = typeName.toLowerCase();
  
  let mappedColor;
  // Check for subtask first (more specific)
  if (CALENDAR_TASK_TYPE_KEYWORDS.SUBTASK.some(kw => lowerType.includes(kw))) mappedColor = CALENDAR_TASK_TYPE_COLOR_MAP.SUBTASK;
  else if (CALENDAR_TASK_TYPE_KEYWORDS.BUG.some(kw => lowerType.includes(kw))) mappedColor = CALENDAR_TASK_TYPE_COLOR_MAP.BUG;
  else if (CALENDAR_TASK_TYPE_KEYWORDS.FEATURE.some(kw => lowerType.includes(kw))) mappedColor = CALENDAR_TASK_TYPE_COLOR_MAP.FEATURE;
  else if (CALENDAR_TASK_TYPE_KEYWORDS.TASK.some(kw => lowerType.includes(kw))) mappedColor = CALENDAR_TASK_TYPE_COLOR_MAP.TASK;
  else if (CALENDAR_TASK_TYPE_KEYWORDS.STORY.some(kw => lowerType.includes(kw))) mappedColor = CALENDAR_TASK_TYPE_COLOR_MAP.STORY;
  else if (CALENDAR_TASK_TYPE_KEYWORDS.EPIC.some(kw => lowerType.includes(kw))) mappedColor = CALENDAR_TASK_TYPE_COLOR_MAP.EPIC;
  
  if (mappedColor) {
    return mappedColor;
  }
  return generateColorFromString(typeName, 'taskType');
};

export const getAssigneeColor = (assignee) => {
  const assigneeName = typeof assignee === 'string' ? assignee : assignee?.name || '';
  return generateColorFromString(assigneeName, 'assignee');
};

// Calendar Color Legend Component
export const CalendarColorLegend = ({ colorCodeOption, tasks, dateRange }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const dropdownRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);
  
  if (!colorCodeOption || !tasks || tasks.length === 0) return null;

  const getUniqueValues = () => {
    const uniqueMap = new Map();
    
    // Filter tasks to only include those in the current date range
    const filteredTasks = dateRange ? tasks.filter(task => {
      if (!task.startDate) return false;
      return task.startDate >= dateRange.fromDate && task.startDate <= dateRange.toDate;
    }) : tasks;
    
    filteredTasks.forEach(task => {
      let value, color;
      
      if (colorCodeOption === 'priority') {
        value = typeof task.priority === 'string' ? task.priority : task.priority?.name;
        color = getPriorityColor(task.priority);
      } else if (colorCodeOption === 'status') {
        value = typeof task.status === 'string' ? task.status : task.status?.name;
        color = getStatusColor(task.status);
      } else if (colorCodeOption === 'taskType') {
        value = typeof task.taskType === 'string' ? task.taskType : task.taskType?.name;
        color = getTaskTypeColor(task.taskType);
      }
      
      if (value && !uniqueMap.has(value)) {
        uniqueMap.set(value, color);
      }
    });
    
    return Array.from(uniqueMap.entries()).map(([name, color]) => ({ name, color }));
  };

  const items = getUniqueValues();
  if (items.length === 0) return null;

  const getTitle = () => CALENDAR_COLOR_OPTION_LABELS[colorCodeOption] || '';

  return (
    <Box className="color-legend" sx={{ position: 'relative' }} ref={dropdownRef}>
      <Box className="color-legend-header" onClick={() => setIsExpanded(!isExpanded)} sx={{ cursor: 'pointer' }}>
        <Typography className="color-legend-title">{t('calendar.legend')} {getTitle()}</Typography>
        <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
          {isExpanded ? <KeyboardArrowUp sx={{ fontSize: 16 }} /> : <KeyboardArrowDown sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>
      {isExpanded && (
        <Box className="color-legend-dropdown">
          {items.map(({ name, color }) => (
            <Box key={name} className="color-legend-item">
              <Box className="color-legend-dot" sx={{ backgroundColor: color }} />
              <OverflowTooltip title={name} tooltipWidth={250} placement="top" component={Typography} className="color-legend-label">
                {name}
              </OverflowTooltip>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
export function hexToRgba(hex, alpha = 1) {
  if (!hex || typeof hex !== 'string') return `rgba(99,102,106,${alpha})`;
  if (!hex.startsWith('#')) return hex;
  const h = hex.replace('#','');
  const r = parseInt(h.length===3 ? h[0]+h[0] : h.substring(0,2), 16);
  const g = parseInt(h.length===3 ? h[1]+h[1] : h.substring(h.length===3?1:2, h.length===3?2:4), 16);
  const b = parseInt(h.length===3 ? h[2]+h[2] : h.substring(4,6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
